import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Button from "./Button";

const MENU_WIDTH = 230;

// Dropdown rendered in a portal so table/overflow containers can't clip it.
// items: [{ key, label, icon, onSelect, danger, divider }]
const Menu = ({ items, label, icon, iconOnly = false, variant = "secondary", size, ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight || 200;
    const openUp = rect.bottom + menuHeight + 12 > window.innerHeight && rect.top > menuHeight;
    setPos({
      top: openUp ? rect.top - menuHeight - 6 : rect.bottom + 6,
      left: Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8)),
      origin: openUp ? "bottom right" : "top right",
    });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!menuRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) close();
    };
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open, close]);

  return (
    <>
      <span ref={triggerRef} className="cc-menu-trigger">
        <Button
          variant={variant}
          size={size}
          icon={icon}
          iconOnly={iconOnly}
          aria-label={ariaLabel || label}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          {label}
        </Button>
      </span>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="cc-menu cc-menu--floating cc-bb"
            role="menu"
            style={{
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              width: MENU_WIDTH,
              transformOrigin: pos?.origin,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {items.map((item) =>
              item.divider ? (
                <div key={item.key} className="cc-menu__divider" />
              ) : (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  className={`cc-menu__item${item.danger ? " cc-menu__item--danger" : ""}`}
                  onClick={() => {
                    close();
                    item.onSelect?.();
                  }}
                >
                  {item.icon && <item.icon size={16} aria-hidden="true" />}
                  {item.label}
                </button>
              )
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export default Menu;
